import { type InvestmentsReportTemplate, type PageDto, PdfControllerApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { type RequireProperty, downloadBlob } from "$root/utils";
import type { TemplateChooserSubmitParams } from "./portfolio-studio/dialogs/TemplateChooserDialog";
import { isDefaultReportTemplate } from "./reports/default-templates";
import { formatDate } from "$root/localization/formatters";

/** @param asOf pinned date (`YYYY-MM-DD`) to name the file after, read in UTC; today when absent. */
export function generateReportBuilderTemplateName(
	pageMetadata: TemplateChooserSubmitParams,
	templateName: string,
	asOf?: string,
): string {
	const { name, choice } = pageMetadata;
	const date = asOf ? formatDate(asOf, { timezone: "UTC" }) : formatDate(new Date());
	return `${name ? `${name}_` : ""}${templateName}_${date}${choice === "current" ? "" : "proposal_ready"}`;
}

export type HealthyPageDto = RequireProperty<PageDto, "name" | "objectId" | "templateId">;
export function generateReportBuilderTemplateUrl(
	metadata: Array<TemplateChooserSubmitParams>,
	template: InvestmentsReportTemplate,
	opts: { asOf: string | undefined },
): HealthyPageDto[] {
	return metadata.map(({ choice, uuid, name }): HealthyPageDto => {
		// A proposal is a live, forward-looking object: it has no stored snapshot for a past day, so the
		// date reaches the current pages only. One dialog can ask for both at once — current at a date,
		// proposal as it stands — and this is where those two come apart.
		const asOf = choice === "current" ? opts.asOf : undefined;
		return {
			variant: choice === "current" ? "current" : "proposal",
			objectId: uuid,
			templateId: isDefaultReportTemplate(template) ? template.id : template.uuid!,
			name: generateReportBuilderTemplateName({ choice, uuid, name }, template.templateName ?? "-", asOf),
			// Truthy, not `!= null`: the server reads an empty `asOfDate` as present and appends a
			// valueless query param to the URL it renders.
			...(asOf ? { asOfDate: asOf } : {}),
		};
	});
}

export async function downloadSinglePdf(name: string, conf: PageDto): Promise<void> {
	const pdfV3Api = getApiGen(PdfControllerApiFactory);

	const pdf = await axiosExtract(pdfV3Api.generateCustomReportPdf(name, conf, { responseType: "blob" }));
	downloadBlob(new Blob([pdf]), { fileName: `${name}.pdf` });
}
